/**
 * Created by katarinababic on 13.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTrans } from '../../Store/System/LocalizationStore';
import { HomeScreenStats } from './HomeScreenStats';
import { HomeScreenSuggestions } from './HomeScreenSuggestions';
import { HomeScreenScheduledActivities } from './HomeScreenScheduledActivities';
import { ActiveChallengeCard } from './ActiveChallengeCard';
import { useActiveChallenge } from '../../Hooks/useActiveChallenge';
import { LogActivityModal } from './LogActivityModal';
import academyImage from '../../Assets/Images/academy.png';
import movementImage from '../../Assets/Images/movement.png';
import mentalHealthImage from '../../Assets/Images/mental_health.png';
import nutritionImage from '../../Assets/Images/nutrition.png';
import { Translation } from '../../Components/Translation';
import { Theme } from '../../Theme/Theme';
import { CompanyAreaCard } from '../../Components/CompanyAreaCard';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { ServiceLifecycleCard } from '../../Components/ServiceLifecycleBoundary';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const HomePage = styled.div`
  background: ${Theme.Colors.white};
`;

const HomeScreenContainer = styled(Container)`
  margin: 0 auto;
  padding: 48px 12px 64px;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: 768px) {
    padding: 32px 16px 48px;
  }
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  color: #00dda2;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0 0 24px 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const SectionHeader = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 24px;
  color: ${Theme.Colors.textColor};
`;

const ListContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  margin-top: 48px;
`;

const ExploreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 48px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ExploreCard = styled.div`
  border-radius: 16px;
  background: white;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const ExploreImage = styled.img`
  width: 100%;
  height: 160px;
  object-fit: cover;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

const CardTitle = styled.p`
  font-weight: 600;
  font-size: 14px;
  color: ${Theme.Colors.textColor};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  margin-top: 48px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const FullWidthSection = styled.div`
  margin-top: 48px;
`;

export type HomeScreenProps = {};

export const HomeScreen: React.FC<HomeScreenProps> = observer((props) => {
  const { t } = useTrans();
  const navigate = useNavigate();
  const { data: activeChallenge } = useActiveChallenge();

  const [logActivityModalOpen, setLogActivityModalOpen] = useState(false);

  const handleCloseLogActivity = useCallback(() => setLogActivityModalOpen(false), []);

  return (
    <HomePage>
      {/* Hero Section */}
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <AccentText>{t('home.welcomeHeroAccent', { defaultValue: 'Your Health Journey' })}</AccentText>{' '}
            {t('home.welcomeHero', { defaultValue: 'Starts Here' })}
          </HeroTitle>
          <HeroSubtitle>
            {t('home.welcomeSubtitle', { defaultValue: 'Your personal health and fitness companion' })}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <HomeScreenContainer>
        <ServiceLifecycleCard inContent />
        {/* Company Area - Exclusive content for the user's company */}
        <CompanyAreaCard />

        {/* Explore Content - Visually Prominent */}
        <SectionHeader>
          <Translation
            i18nKey="home.exploreTitle"
            defaultValue={`<span style="color: #00dda2">Explore</span> our content`}
          />
        </SectionHeader>
        <ExploreGrid>
          {[
            { image: movementImage, label: t('home.movement', { defaultValue: 'Movement' }), path: '/move' },
            {
              image: mentalHealthImage,
              label: t('home.mentalHealth', { defaultValue: 'Mental Health' }),
              path: '/mental-health',
            },
            { image: nutritionImage, label: t('home.nutrition', { defaultValue: 'Nutrition' }), path: '/nutrition' },
            { image: academyImage, label: t('home.academy', { defaultValue: 'Academy' }), path: '/academy' },
          ].map((item) => (
            <ExploreCard key={item.path} onClick={() => navigate(item.path)}>
              <ExploreImage src={item.image} alt={item.label} />
              <CardContent>
                <CardTitle>{item.label}</CardTitle>
              </CardContent>
            </ExploreCard>
          ))}
        </ExploreGrid>

        {/* Challenge Card */}
        {activeChallenge && (
          <ListContainer style={{ marginTop: 32 }}>
            <SectionHeader>
              <Translation
                i18nKey="home.challengeTitle"
                defaultValue={`<span style="color: #00dda2">Your</span> Challenge`}
              />
            </SectionHeader>
            <ActiveChallengeCard />
          </ListContainer>
        )}

        {/* Suggestions & Scheduled Activities - Side by Side */}
        <TwoColumnGrid>
          <HomeScreenSuggestions />
          <HomeScreenScheduledActivities />
        </TwoColumnGrid>

        {/* Statistics - Full Width */}
        <FullWidthSection>
          <HomeScreenStats />
        </FullWidthSection>

        <LogActivityModal isOpen={logActivityModalOpen} onClose={handleCloseLogActivity} />
      </HomeScreenContainer>
    </HomePage>
  );
});
